import React, { useState } from 'react';
import { Card, CardBody, Collapse } from 'reactstrap';
import { formatCurrency } from '../../../../utils/format';
import { formatDate } from '../../../../utils/date';
import { useTenant } from "../../../../hooks/useTenant";
import { FINANCIAL_TYPES } from '../../../../constants/financial';

export const CashierTable = ({ records, loading, summary, selectedDate, currentShift, onCloseCashier }) => {
    const { user } = useTenant();
    const [isOpen, setIsOpen] = useState(true);
    const [expandedRecord, setExpandedRecord] = useState(null);

    const toggleOpen = () => setIsOpen(!isOpen);
    const toggleRecord = (id) => setExpandedRecord(expandedRecord === id ? null : id);

    const userName = user?.name ? user.name.toUpperCase() : "CAIXA PRINCIPAL";
    const openDate = selectedDate.split('-').reverse().join('/');

    const consolidatedRecords = React.useMemo(() => {
        if (!records) return [];
        const groups = {};
        const result = [];

        records.forEach(r => {
            // Agrupar apenas registros que pertencem à mesma venda e mesma forma de pagamento
            if (r.idSale && r.idSale.trim() !== '') {
                const key = `${r.idSale}_${r.paymentMethod}`;
                if (!groups[key]) {
                    groups[key] = { 
                        ...r, 
                        isGrouped: false, 
                        groupedCount: 1, 
                        totalAmount: r.netAmount || 0 
                    };
                    result.push(groups[key]);
                } else {
                    groups[key].totalAmount += (r.netAmount || 0);
                    groups[key].groupedCount += 1;
                    groups[key].isGrouped = true;
                    // Limpar a descrição para agrupar (ex: Venda #01011 — Parcelado (12x))
                    const descParts = groups[key].description?.split('—') || [];
                    const baseDesc = descParts[0]?.trim() || `Venda ${r.idSale.substring(r.idSale.length - 6)}`;
                    groups[key].description = `${baseDesc} — Parcelado (${groups[key].groupedCount}x)`;
                    groups[key].dueDate = null; 
                }
            } else {
                result.push({ ...r, totalAmount: r.netAmount || 0 });
            }
        });

        result.forEach(item => {
            if (item.isGrouped) {
                item.netAmount = item.totalAmount;
            }
        });

        return result;
    }, [records]);

    return (
        <Card className="border shadow-none mb-4" style={{ borderRadius: '8px' }}>
            {/* ═══ ACCORDION HEADER (FAKE SHIFT) ═══ */}
            <div 
                className="d-flex align-items-center justify-content-between p-3" 
                style={{ backgroundColor: '#fff', borderBottom: isOpen ? '1px solid #eff2f7' : 'none', cursor: 'pointer' }}
                onClick={toggleOpen}
            >
                <div className="d-flex align-items-center">
                    <i className={`mdi mdi-chevron-${isOpen ? 'down' : 'right'} font-size-24 text-muted me-3`} />
                    <div>
                        <h5 className="mb-1" style={{ fontSize: '0.95rem', fontWeight: '700', color: '#495057' }}>{userName}</h5>
                        <div className="d-flex align-items-center flex-wrap gap-3 text-muted" style={{ fontSize: '0.8rem' }}>
                            <span><strong>Abertura:</strong> {openDate} 07:17</span>
                            <span><strong>Valor inicial:</strong> R$ 0,00</span>
                            <span><strong>Valor Recebimentos:</strong> {formatCurrency(summary.totalIncome)}</span>
                            <span><strong>Valor Saída:</strong> {formatCurrency(summary.totalExpense)}</span>
                            <span><strong>Valor Final:</strong> {formatCurrency(summary.balanceOfDay)} <i className="mdi mdi-help-circle-outline ms-1" /></span>
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {currentShift && currentShift.status === "OPEN" && (
                        <span 
                            className="fw-semibold text-primary cursor-pointer" 
                            style={{ fontSize: '0.85rem' }} 
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                if (onCloseCashier) onCloseCashier(); 
                            }}
                        >
                            Fechar caixa
                        </span>
                    )}
                    {currentShift && currentShift.status === "CLOSED" && (
                        <span className="badge bg-danger rounded-0 fw-medium">CAIXA FECHADO</span>
                    )}
                    <h5 className="mb-0 fw-bold" style={{ fontSize: '1rem' }}>
                        {formatCurrency((currentShift?.openingBalance || 0) + summary.balanceOfDay)}
                    </h5>
                </div>
            </div>

            {/* ═══ TABLE CONTENT ═══ */}
            <Collapse isOpen={isOpen}>
                <CardBody className="p-0">
                    <div className="table-responsive">
                        <table className="table align-middle mb-0 table-hover" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#fcfcfc', borderBottom: '1px solid #eff2f7' }}>
                                    <th style={thStyle}>Nome</th>
                                    <th style={thStyle}>Formas de pagamento</th>
                                    <th style={thStyle}>Descrição</th>
                                    <th style={thStyle}>Lançamento</th>
                                    <th style={thStyle}>Vencimento</th>
                                    <th style={thStyle}>Tipo</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Linha de Abertura Fixa (Visual Mock) */}
                                <tr>
                                    <td style={tdStyle}></td>
                                    <td style={{...tdStyle, color: '#74788d'}}>-</td>
                                    <td style={{...tdStyle, color: '#74788d', fontSize: '0.8rem'}}>Abertura de caixa</td>
                                    <td style={{...tdStyle, color: '#74788d', fontSize: '0.8rem'}}>{openDate}</td>
                                    <td style={{...tdStyle, color: '#74788d'}}>-</td>
                                    <td style={tdStyle}>
                                        <span className="badge rounded-0 bg-soft-info text-info fw-medium px-2 py-1">
                                            Abertura
                                        </span>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: '#74788d', fontSize: '0.8rem' }}>
                                        {formatCurrency(currentShift?.openingBalance || 0)}
                                    </td>
                                </tr>

                                {!consolidatedRecords || consolidatedRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted" style={{ fontSize: '0.85rem' }}>
                                            {loading ? "Carregando movimentações..." : "Nenhuma movimentação para esta data."}
                                        </td>
                                    </tr>
                                ) : (
                                    consolidatedRecords.map((r) => {
                                        const isIncome = r.type === FINANCIAL_TYPES.INCOME;
                                        const isExpanded = expandedRecord === r.id;

                                        return (
                                            <React.Fragment key={r.id}>
                                                <tr onClick={() => toggleRecord(r.id)} style={{ cursor: 'pointer' }}>
                                                    <td style={{ ...tdStyle, fontSize: '0.8rem', fontWeight: '500' }} className="text-primary">
                                                        <i className={`mdi mdi-chevron-${isExpanded ? 'up' : 'down'} text-muted me-2`} />
                                                        {r.entityName?.toUpperCase() || r.description?.toUpperCase() || '-'}
                                                    </td>
                                                    <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#495057' }}>
                                                        <i className="mdi mdi-cash text-muted me-2" />
                                                        {r.paymentMethod || 'Diversos'}
                                                    </td>
                                                    <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#495057' }}>
                                                        {r.description || '-'}
                                                    </td>
                                                    <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#495057' }}>
                                                        {formatDate(r.paymentDate)}
                                                    </td>
                                                    <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#495057' }}>
                                                        {r.isGrouped ? '-' : formatDate(r.dueDate)}
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <span 
                                                            className={`badge rounded-0 px-2 py-1 fw-medium ${isIncome ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'}`}
                                                        >
                                                            {isIncome ? 'Recebimento' : 'Saída'}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'right', fontSize: '0.85rem', color: '#495057' }}>
                                                        {formatCurrency(r.netAmount)}
                                                    </td>
                                                </tr>
                                                {/* Expanded Details Row */}
                                                {isExpanded && (
                                                    <tr style={{ backgroundColor: '#fcfcfc' }}>
                                                        <td colSpan="7" className="p-4">
                                                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                <span className="me-3">Data da venda: {formatDate(r.paymentDate)}</span>
                                                                <span>/ &nbsp;&nbsp;&nbsp;</span>
                                                                <span className="text-primary ms-3" style={{ textDecoration: 'underline', cursor: 'pointer' }}>ID da venda: {r.idSale || r.id.substring(0,6)}</span>
                                                            </div>
                                                            <div className="mt-3 d-flex justify-content-between align-items-center">
                                                                <span className="fw-semibold" style={{ fontSize: '0.8rem', color: '#495057' }}>
                                                                    Serviço/Produto - {r.description?.toUpperCase()}
                                                                </span>
                                                                <span style={{ fontSize: '0.85rem', color: '#495057' }}>
                                                                    {formatCurrency(r.netAmount)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}

                                {/* FOOTER TOTAL ROW */}
                                <tr>
                                    <td colSpan="7" className="px-4 py-3 border-top">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span className="fw-bold d-flex align-items-center" style={{ fontSize: '0.85rem', color: '#495057' }}>
                                                <i className="mdi mdi-chevron-right me-2" /> Total
                                            </span>
                                            <span className="fw-bold" style={{ fontSize: '0.9rem', color: '#495057' }}>
                                                {formatCurrency(summary.balanceOfDay)}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        
                        {/* PAGINATION / FOOTER INFO */}
                        <div className="d-flex align-items-center justify-content-end px-3 py-2" style={{ backgroundColor: '#f9f9fc', borderTop: '1px solid #eff2f7' }}>
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>Itens por página</span>
                                <select className="form-select form-select-sm border-0 bg-transparent text-muted" style={{ width: '60px', paddingLeft: 0, fontSize: '0.8rem' }}>
                                    <option>20</option>
                                    <option>50</option>
                                </select>
                                <span className="text-muted mx-2" style={{ fontSize: '0.8rem' }}>
                                    1 - {consolidatedRecords.length || 0} de {consolidatedRecords.length || 0}
                                </span>
                                <div className="d-flex gap-2">
                                    <i className="mdi mdi-chevron-left text-muted font-size-18" style={{ cursor: 'pointer' }} />
                                    <i className="mdi mdi-chevron-right text-muted font-size-18" style={{ cursor: 'pointer' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </CardBody>
            </Collapse>
        </Card>
    );
};

const thStyle = {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: "#74788d",
    padding: "12px 16px",
    borderBottom: "1px solid #eff2f7",
    whiteSpace: "nowrap",
};

const tdStyle = {
    padding: "12px 16px",
    verticalAlign: "middle",
    borderBottom: "1px solid #eff2f7",
};

